import { EChartSvg } from "../../charts/EChartSvg";
import {
  histogramOption,
  interannualOption,
  landUseOption,
  weibullOption,
  windRoseOption,
} from "../charts/options";
import type {
  ResourceCharacterizationResponse,
  ResourceVariable,
} from "../../../services/api";
import { DataTable, Figure, INK, Lead, SubTitle, Unavailable, nf } from "./primitives";

const GROUP_TITLE: Record<ResourceVariable["group"], string> = {
  solar: "Recurso solar",
  eolico: "Recurso eólico",
  terreno: "Terreno",
};

function digitsFor(v: ResourceVariable): number {
  return v.max != null && v.min != null && v.max - v.min < 10 ? 2 : 1;
}

export function ResourceTable({ variables }: { variables: ResourceVariable[] }) {
  return (
    <DataTable
      columns={[
        {
          header: "Variável",
          width: "38%",
          render: (v: ResourceVariable) => (
            <>
              <div>{v.label}</div>
              <div className="text-[0.68rem]" style={{ color: INK.secondary }}>
                {v.description}
              </div>
            </>
          ),
        },
        { header: "Unidade", render: (v) => v.unit || "—" },
        {
          header: "Média",
          align: "right",
          render: (v) => (v.available ? nf(v.mean, digitsFor(v)) : "indisponível"),
        },
        {
          header: "P10 – P90",
          align: "right",
          render: (v) =>
            v.available ? `${nf(v.p10, digitsFor(v))} – ${nf(v.p90, digitsFor(v))}` : "—",
        },
        {
          header: "Mín. – Máx.",
          align: "right",
          render: (v) =>
            v.available ? `${nf(v.min, digitsFor(v))} – ${nf(v.max, digitsFor(v))}` : "—",
        },
      ]}
      rows={variables}
      rowKey={(v) => v.id}
    />
  );
}

function variable(
  c: ResourceCharacterizationResponse,
  id: string
): ResourceVariable | undefined {
  const v = c.variables.find((x) => x.id === id);
  return v?.available ? v : undefined;
}

function HistogramFigure({
  v,
  caption,
  reference,
}: {
  v: ResourceVariable | undefined;
  caption: string;
  reference?: { label: string; value: number } | null;
}) {
  if (!v || v.histogram.length === 0) return null;
  return (
    <Figure caption={caption}>
      <EChartSvg
        ariaLabel={`Distribuição de ${v.label}`}
        width={300}
        height={170}
        option={histogramOption(v.histogram, v.unit, reference)}
      />
    </Figure>
  );
}

export function ResourceSection({
  characterization,
  stateCharacterization,
}: {
  characterization: ResourceCharacterizationResponse | null;
  /** Médias de Pernambuco, desenhadas como linha de referência nos histogramas. */
  stateCharacterization?: ResourceCharacterizationResponse | null;
}) {
  if (!characterization) {
    return (
      <>
        <SubTitle>Caracterização do recurso</SubTitle>
        <Unavailable what="Caracterização do recurso" />
      </>
    );
  }
  const c = characterization;
  const stateMean = (id: string) => {
    const v = stateCharacterization ? variable(stateCharacterization, id) : undefined;
    return v?.mean != null ? { label: "média PE", value: v.mean } : null;
  };
  const groups = (["solar", "eolico", "terreno"] as const).filter((g) =>
    c.variables.some((v) => v.group === g)
  );

  const weibullA = variable(c, "weibull_a")?.mean;
  const weibullK = variable(c, "weibull_k")?.mean;

  return (
    <>
      <SubTitle>Caracterização do recurso</SubTitle>
      <Lead>
        Estatísticas dos dados aeroespaciais dentro da área (grade de 200 m; variáveis
        eólicas a {c.wind_height_m} m). P10 e P90 delimitam os 80% centrais dos pixels.
        Esta seção é descritiva: o cálculo de geração usa as camadas do memorial acima.
      </Lead>

      {groups.map((group) => (
        <div key={group}>
          <p className="text-[0.8rem] font-semibold mt-3 mb-1" style={{ color: INK.brand }}>
            {GROUP_TITLE[group]}
          </p>
          <ResourceTable variables={c.variables.filter((v) => v.group === group)} />
        </div>
      ))}

      <div className="grid grid-cols-2 gap-x-4">
        {c.energy === "solar" ? (
          <>
            <HistogramFigure
              v={variable(c, "ghi")}
              caption="Distribuição do GHI na área (% dos pixels por faixa)."
              reference={stateMean("ghi")}
            />
            <HistogramFigure
              v={variable(c, "temperatura_max")}
              caption="Distribuição da temperatura máxima média na área."
            />
          </>
        ) : (
          <>
            <HistogramFigure
              v={variable(c, "velocidade")}
              caption={`Distribuição da velocidade média a ${c.wind_height_m} m entre os pixels da área.`}
              reference={stateMean("velocidade")}
            />
            {weibullA != null && weibullK != null && (
              <Figure
                caption={`Distribuição de Weibull com A = ${nf(weibullA, 2)} m/s e k = ${nf(
                  weibullK,
                  2
                )} (médias da área): fração do tempo em cada velocidade.`}
              >
                <EChartSvg
                  ariaLabel="Curva de Weibull da área"
                  width={300}
                  height={170}
                  option={weibullOption(weibullA, weibullK)}
                />
              </Figure>
            )}
            {c.wind_rose.length > 0 && (
              <Figure caption={`Rosa dos ventos a ${c.wind_height_m} m: frequência de ocorrência por setor de 30°.`}>
                <EChartSvg
                  ariaLabel="Rosa dos ventos"
                  width={300}
                  height={240}
                  option={windRoseOption(c.wind_rose)}
                />
              </Figure>
            )}
            {c.interannual.length > 0 && (
              <Figure caption="Variabilidade interanual: índice de vento de cada ano (1 = média histórica).">
                <EChartSvg
                  ariaLabel="Variabilidade interanual do vento"
                  width={300}
                  height={170}
                  option={interannualOption(c.interannual)}
                />
              </Figure>
            )}
          </>
        )}
        <HistogramFigure
          v={variable(c, "declividade")}
          caption="Distribuição da declividade do terreno na área."
        />
        {c.land_use.length > 0 && (
          <Figure caption="Uso do solo: % da área por classe (a Aeroespacial ainda não forneceu os nomes das classes).">
            <EChartSvg
              ariaLabel="Uso do solo por classe"
              width={300}
              height={Math.max(120, 32 + 18 * c.land_use.length)}
              option={landUseOption(c.land_use)}
            />
          </Figure>
        )}
      </div>
    </>
  );
}
